import Button from '@components/Button';
import SkuContext from '@components/SkuContext';
import cn from 'classnames';
import FocusTrap from 'focus-trap-react/dist/focus-trap-react';
import groupBy from 'lodash.groupby';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
} from 'react-accessible-accordion';
import { Transition } from 'react-transition-group';
import * as styles from './styles';

const FinishModal = ({
  activeFinishCode,
  productFinishes,
  closeModal,
  modalContainerScroll,
  offset,
  open,
  optionIndex,
  optionName,
  possibleFinishes,
}) => {
  const updateUrl = useContext(SkuContext);
  const modalContainer = useRef(null);
  const [klaviyoFormOpen, setKlaviyoFormOpen] = useState(JSON.parse(localStorage.getItem("klopen")));

  useEffect(() => {
    function handleKlaviyo(e) {
      if (e.detail?.type === 'close' && e.detail.formId === 'Y62HX8') {
        setKlaviyoFormOpen(false);
        localStorage.setItem("klopen", "false")
      }
    }
  
    window.addEventListener('klaviyoForms', handleKlaviyo);
    return () => window.removeEventListener('klaviyoForms', handleKlaviyo);
  }, []); 

  useEffect(() => {
    if (modalContainerScroll && modalContainer.current) {
      console.dir(modalContainerScroll);
      console.dir(modalContainer.current);
      setTimeout(() => {
        modalContainer.current.scrollTo({
          top: modalContainerScroll,
          behavior: 'smooth', // Enables smooth scrolling
        });
      }, 0);
      console.dir(modalContainer.current);
    }
  }, [modalContainerScroll]);

  const result = productFinishes.filter(
    (el) => possibleFinishes[el.finish.code],
  );

  const categories = groupBy(result, (e) => {
    return e.finish?.category?.title;
  });

  const openKlForm = (finish) => {
    // If the finish is custom powder coated (PCXX), then show the klaviyo popup
    if (finish.finish.code === 'PCXX') { 
      // @ts-ignore
      window._klOnsite = window._klOnsite || [];
      // @ts-ignore
      window._klOnsite.push(['openForm', 'Y62HX8']);
      setKlaviyoFormOpen(true);
      localStorage.setItem("klopen", "true")
    }
    return;
  } 

  const onFinishClick = (finish) => {
    openKlForm(finish);

    if (window.innerWidth >= 768) {
      updateUrl(
        finish.finish.code,
        optionIndex,
        offset,
        modalContainer.current.scrollTop || 0,
      );
    } else {
      updateUrl(finish.finish.code, optionIndex);
    }
  }

  const finishCategories = () => {
    return Object.keys(categories).map((el, index, arr) => {
      const formattedCategory = categories[el].map((fn) => ({
        ...fn,
        section: fn.section || 'Others',
      }));
      const sections = groupBy(formattedCategory, (fn) => fn.section);

      return (
        <AccordionItem key={el} uuid={el}>
          <AccordionItemHeading>
            <AccordionItemButton
              className={cn(styles.accordionItemButton, {
                '!border-b': index === arr.length - 1,
              })}
            >
              {el}
            </AccordionItemButton>
          </AccordionItemHeading>
          <AccordionItemPanel
            className={cn(styles.accordionItemPanel, {
              '!pt-0': el.toLowerCase() === 'options for customization',
            })}
          >
            {Object.keys(sections).map((section) => (
              <div className="mb-4 last-of-type:mb-0">
                <div
                  className={cn('flex gap-2 items-center mb-2 leading-none', {
                    hidden:
                      el.toLowerCase() === 'options for customization' &&
                      section.toLowerCase() === 'others',
                  })}
                >
                  <span className="w-3 aspect-square rounded-full block bg-mono-300 ml-3 lg:ml-7 -mt-0.5" />
                  <p className="type-sans-330 text-mono-400">{section}</p>
                </div>
                <div
                  className={cn('has-delay-container', styles.finishWrapper)}
                >
                  {sections[section].map((finish) => {
                    return finish.finish.code === activeFinishCode ? (
                      <div
                        className={cn('has-delay', styles.finishPickerCard)}
                        key={finish.finish.id}
                        onClick={() => openKlForm(finish)}
                      >
                        <div
                          className={cn(
                            'bg-mono-100',
                            styles.finishPickerCardInner,
                          )}
                        >
                          <img
                            src={finish.finish.image.asset.url}
                            alt={finish.finish.image.altText}
                            className="w-full"
                          />
                          <p className="w-full">{finish.finish.title}</p>
                        </div>
                      </div>
                    ) : (
                      <div
                        className={cn('has-delay', styles.finishPickerCard)}
                        key={finish.finish.id}
                      >
                        <Button
                          aria-label={finish.finish.title}
                          onClick={() => onFinishClick(finish)}
                          className={cn(
                            'transition-opacity hover:opacity-75 ease-in duration-300 w-full',
                            styles.finishPickerCardInner,
                          )}
                        >
                          <img
                            src={finish.finish.image.asset.url}
                            alt={finish.finish.image.altText}
                            className="w-full"
                          />
                          <p className="w-full">{finish.finish.title}</p>
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </AccordionItemPanel>
        </AccordionItem>
      );
    });
  };

  return (
    <div className={cn(styles.modal, { hidden: !open || klaviyoFormOpen })}>
      <button
        className={styles.modalBackground}
        onClick={() => {
          closeModal();
        }}
        type="button"
        aria-label="close"
      />
      <Transition in={open} timeout={100}>
        {(transitionState) => (
          <>
            {open && !klaviyoFormOpen && (
              <FocusTrap
                focusTrapOptions={{
                  onDeactivate: closeModal,
                  allowOutsideClick: () => true,
                }}
              >
                <div
                  className={cn(styles.modalContent, styles[transitionState])}
                >
                  <div
                    className="h-full relative overflow-y-auto "
                    ref={modalContainer}
                  >
                    <p className="type-upper-140 px-5 py-6">{optionName}</p>
                    <Accordion
                      allowMultipleExpanded
                      allowZeroExpanded
                      preExpanded={Object.keys(categories).map((el) => el)}
                    >
                      {finishCategories()}
                    </Accordion>
                  </div>
                  <div className="fixed bottom-0 left-0 w-full flex border-mono-300 border-t">
                    <Button
                      className="btn-default btn-white"
                      aria-label="close"
                      onClick={() => {
                        closeModal();
                      }}
                      icon="close"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </FocusTrap>
            )}
          </>
        )}
      </Transition>
    </div>
  );
};

export default FinishModal;
